<template>
  <div class="file-view">
    <BannedView v-if="isBanned" :file-url="fileUrl" />

    <template v-else>
      <DownloadView
        v-if="isDownload"
        :file-url="fileUrl"
        :easy-file="easyFile"
        @download="clickDownloadFile"
      />

      <MiniProgramRedirect
        v-else-if="isMobile && isWebToMpRedirectEnable"
        @open-miniprogram="clickOpenMiniprogramBtn"
      />

      <FileViewer
        v-if="shouldRenderFileViewer"
        :file-url="fileUrl"
        :file-type="fileType"
        :easy-file="easyFile"
      />

      <IconReport class="report-icon" v-if="true" @click="clickReportIcon" />
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, watchEffect } from "vue";
import { useEasyFile } from "@/composables/easyfile/useEasyFile";
import IconReport from "@/components/icons/IconReport.vue";
import BannedView from "./components/BannedView.vue";
import DownloadView from "./components/DownloadView.vue";
import MiniProgramRedirect from "./components/MiniProgramRedirect.vue";
import FileViewer from "./components/FileViewer.vue";
import Tracking from "@/model/tracking.js";
// import { useInterstitialAd } from "@/composables/ads/useInterstitialAd";

const props = defineProps({
  relativeUrl: {
    type: String,
    required: true,
  },
});

const {
  state: {
    fileUrl,
    fileType,
    easyFile,
    isDownload,
    isMobile,
    isBanned,
    isWebToMpRedirectEnable,
  },
  action: { handleReport, handleOpenMiniProgram, handleDownload, initialize },
} = useEasyFile(props.relativeUrl);

// const { tryRenderInterstitialAd } = useInterstitialAd();
// const adRenderedThisView = ref(false);

const DEFAULT_TITLE = "简单云链 EasyLink | 免费文件转链接工具 二维码生成器";
const DEFAULT_DESCRIPTION = "让每个人都拥有自己的数字资产——万物皆可转为短链、二维码，轻松分享、售卖、消费。";
const DEFAULT_CANONICAL_URL = "https://easylink.cc/";

function updateMetaTag(name, content) {
  console.log(`[EasyFileView] updateMetaTag called for name: ${name}, content: ${content}`);
  document.querySelectorAll(`meta[name="${name}"]`).forEach(tag => {
    console.log(`[EasyFileView] Removing existing meta[name="${name}"] with content: ${tag.content}`);
    tag.remove();
  });
  const newTag = document.createElement('meta');
  newTag.setAttribute('name', name);
  newTag.setAttribute('content', content);
  document.head.appendChild(newTag);
  console.log(`[EasyFileView] Added meta[name="${name}"] with content: ${content}`);
}

function updateLinkTag(rel, href) {
  console.log(`[EasyFileView] updateLinkTag called for rel: ${rel}, href: ${href}`);
  document.querySelectorAll(`link[rel="${rel}"]`).forEach(tag => {
    console.log(`[EasyFileView] Removing existing link[rel="${rel}"] with href: ${tag.href}`);
    tag.remove();
  });
  const newTag = document.createElement('link');
  newTag.setAttribute('rel', rel);
  newTag.setAttribute('href', href);
  document.head.appendChild(newTag);
  console.log(`[EasyFileView] Added link[rel="${rel}"] with href: ${href}`);
}

watchEffect(() => {
  console.log("[EasyFileView] watchEffect for head tags triggered.");
  if (easyFile.value && easyFile.value.name && props.relativeUrl) {
    const fileName = easyFile.value.name;
    const fileUrlSuffix = props.relativeUrl;
    console.log(`[EasyFileView] Setting DYNAMIC head tags for file: ${fileName}, urlSuffix: ${fileUrlSuffix}`);
    document.title = `${fileName} - 简单云链 EasyLink.cc`;
    console.log(`[EasyFileView] Set document.title to: ${document.title}`);
    updateMetaTag("description", `查看/下载文件：${fileName}。由简单云链 EasyLink.cc 提供安全便捷的文件转链接和二维码服务。`);
    updateLinkTag("canonical", `${DEFAULT_CANONICAL_URL}${fileUrlSuffix}`);
  } else {
    console.log("[EasyFileView] Setting DEFAULT head tags.");
    document.title = DEFAULT_TITLE;
    console.log(`[EasyFileView] Set document.title to: ${DEFAULT_TITLE}`);
    updateMetaTag("description", DEFAULT_DESCRIPTION);
    updateLinkTag("canonical", DEFAULT_CANONICAL_URL);
  }
});

const clickReportIcon = () => handleReport(ElMessageBox, ElMessage);

const clickOpenMiniprogramBtn = () => {
  Tracking.send({
    trigger: "button-click",
    page: "jump-to-mini-file-page",
    object: "jump-to-mini-btn",
  });
  handleOpenMiniProgram();
};

const clickDownloadFile = () => {
  Tracking.send({
    trigger: "button-click",
    page: "download-page",
    object: "download-btn",
  });
  handleDownload();
};

const shouldRenderFileViewer = computed(() => {
  return !isBanned.value && !isDownload.value && !(isMobile.value && isWebToMpRedirectEnable.value);
});

// watchEffect(() => {
//   if (shouldRenderFileViewer.value && !adRenderedThisView.value) {
//     console.log("[EasyFileView] Viewer condition met and ad not rendered yet, calling tryRenderInterstitialAd...");
//     const rendered = tryRenderInterstitialAd();
//     if (rendered) {
//       adRenderedThisView.value = true;
//     }
//   } else if (!shouldRenderFileViewer.value) {
//       // 可选：如果需要，可以在这里处理 viewer 条件变为 false 的情况
//       // console.log("[EasyFileView] Viewer condition became false.");
//   }
// });

watch(
  () => props.relativeUrl,
  (newUrl, oldUrl) => {
    if (newUrl !== oldUrl && oldUrl !== undefined) {
      console.log("[EasyFileView] View identifier changed, resetting ad render flag.");
      // adRenderedThisView.value = false;
    }
  },
  { immediate: false }
);

onMounted(async () => {
  console.log("[EasyFileView] Mounted");
  await initialize();

  if (isDownload.value) {
    Tracking.send({
      trigger: "page-view",
      page: "download-page",
    });
  } else if (isMobile.value && isWebToMpRedirectEnable.value) {
    Tracking.send({
      trigger: "page-view",
      page: "jump-to-mini-file-page",
    });
  } else {
    Tracking.send({
      trigger: "page-view",
      page: "file-page",
    });
  }
});
</script>

<style scoped lang="scss">
.file-view {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
}

@media screen and (max-width: 768px) {
  .report-icon {
    position: fixed;
    top: 5.56vw;
    left: 5.56vw;
    height: 3.24vw;
    width: 3.55vw;
    cursor: pointer;
    opacity: 0.5;
    transition: opacity 0.3s ease;

    &:hover {
      opacity: 1;
    }
  }
}

@media screen and (min-width: 769px) {
  .report-icon {
    position: fixed;
    top: 2.78vh;
    left: 1.56vw;
    height: 1.6vh;
    width: 1.76vh;
    cursor: pointer;
    opacity: 0.5;
    transition: opacity 0.3s ease;

    &:hover {
      opacity: 1;
    }
  }
}
</style>
